import { Spacer } from '@ui';
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import useSWR from 'swr';
import { FieldType } from '../../Formular/types';
import { flattenMappableFields } from '../../Formular/helpers/flattenMappableFields';
import { FormMappingRow } from './FormMappingRow';
import { MappingValue, toSourceList } from './formMappingValue';

interface Props {
    formFieldName?: string;
    mappedFields?: Record<string, string>;
    formId?: number;
    description?: string;
    disabledTypes?: FieldType[];
    namePrefix?: string;
    /** Prefilled mapping from the backend; overrides built-in guesses per supplied target. */
    defaultMapping?: Record<string, MappingValue>;
    /**
     * Field types each target accepts, e.g. `{ timeslots: ['table'] }`. Targets not
     * listed keep offering every field. Narrowing the list is what stops a target
     * that can only read one kind of field — a timetable, an amount — from being
     * pointed at a free text answer that will silently resolve to nothing.
     */
    fieldTypesByKey?: Record<string, string[]>;
    /**
     * Hint rendered under a target's select, e.g. what the mapped field is
     * expected to contain. Targets not listed get no hint.
     */
    descriptionsByKey?: Record<string, string>;
    /**
     * Group heading rendered above a target's row, keyed by the first target of
     * the group. Lets one mapping hold targets for different consumers — e.g.
     * invoice fields and participant fields — without two components fighting
     * over the same form value.
     */
    headingsByKey?: Record<string, string>;
    /** Hides the built-in "Formularzuordnung" heading for callers that already provide one. */
    hideLabel?: boolean;
    /** Lets every target be fed by several form fields, combined into one value. */
    multiple?: boolean;
    /** Lets only the named targets combine several fields. */
    multipleKeys?: string[];
    /**
     * Replaces the "joined with a space" sentence under a target that reads its
     * fields separately rather than combining them. Keyed by target.
     */
    joinHintsByKey?: Record<string, string>;
    /**
     * What each selectable field resolves to, per target and then per field path.
     *
     * Only the size question uses it so far, and only because its rows cannot be
     * checked by reading them — see `FormMappingRow`. The order is presentational
     * and never written back into the value.
     */
    slotAnnotationsByKey?: Record<string, Record<string, { note: string; rank: number }>>;
    /** Upper bound on how many form fields may feed one target. */
    maxSources?: number;
    /** Only applies `defaultMapping`, for callers whose backend already guessed the mapping. */
    disableGuessing?: boolean;
}

/**
 * Which part of an address field a target wants, as the dotted path the backend reads
 * back. Null for anything that is not an address field, and for the targets an address
 * has no answer to — a name, an IBAN, the "Adresszusatz" line.
 */
const subPath = (field: { name: string; type: string } | undefined, target: string): string | null => {
    if (field?.type !== 'address') return null;

    const part = { line1: 'street_complete', city: 'city', postcode: 'postalcode', country: 'country' }[target];

    return part ? `${field.name}.${part}` : null;
};

// Renders the mapping selects that link a form's fields to invoice/address data, prefilled either from a
// backend-provided mapping or by guessing from field labels.
export const FormMapping = ({
    formFieldName = 'form',
    namePrefix = 'form_mapping',
    formId: _formId,
    description,
    mappedFields: _mappedFields,
    defaultMapping,
    fieldTypesByKey,
    descriptionsByKey,
    headingsByKey,
    hideLabel = false,
    multiple = false,
    multipleKeys,
    joinHintsByKey,
    slotAnnotationsByKey,
    maxSources,
    disableGuessing = false,
    disabledTypes = ['wysiwyg', 'table', 'checkbox']
}: Props) => {
    const { watch, setValue } = useFormContext();
    const form = watch(formFieldName);
    const currentMapping = watch(namePrefix);
    const formId = _formId ?? (typeof form === 'number' ? form : form?.id);
    // Undefined on purpose. A mapping rendered with an already-selected form
    // still needs one initialisation pass when its form state is empty (the
    // booking-import preview is exactly that case).
    const lastFormId = useRef<number | undefined>(undefined);
    // Serializes defaultMapping by content so an async/re-created-but-unchanged prop doesn't
    // look identical to identity comparisons (the parent re-creates the object every render).
    const defaultMappingKey = defaultMapping ? JSON.stringify(defaultMapping) : undefined;
    // Tracks the serialized defaultMapping that was last applied, so we can detect real content
    // changes instead of re-applying on every render or edit-triggered re-fetch.
    const lastDefaultMappingKey = useRef<string | undefined>(undefined);

    const { data, isLoading } = useSWR(formId ? `form/${formId}` : null);
    const { fields = [] } = data?.data || {};

    // Flatten section-nested fields so nested options appear in the mapping dropdowns.
    const mappableFields = useMemo(
        () => flattenMappableFields(fields, { excludeTypes: disabledTypes }),
        [fields, disabledTypes]
    );

    // Selectable form fields. Address fields hold a nested object, so they get expanded into one
    // option per part (each resolves to a dotted sub-path the backend can read back individually).
    // `allowedTypes` narrows the list for a single target; without it every field stays on offer.
    const buildFieldOptions = useCallback(
        (allowedTypes?: string[]) => [
            { key: '', label: 'Keine Zuordnung' },
            ...mappableFields
                .filter((field) => allowedTypes?.includes(field.type) ?? true)
                .flatMap((field) =>
                    field.type === 'address'
                        ? [
                              { key: `${field.name}.street_complete`, label: `${field.label} (Straße & Hausnummer)` },
                              { key: `${field.name}.street`, label: `${field.label} (Straße)` },
                              { key: `${field.name}.streetnumber`, label: `${field.label} (Hausnummer)` },
                              { key: `${field.name}.postalcode`, label: `${field.label} (Postleitzahl)` },
                              { key: `${field.name}.city`, label: `${field.label} (Stadt)` },
                              { key: `${field.name}.country`, label: `${field.label} (Land)` }
                          ]
                        : [{ key: field.name, label: field.label }]
                )
        ],
        [mappableFields]
    );

    const mappedFields = _mappedFields ?? {
        line0: 'Name / Firma',
        line1: 'Straße',
        line2: 'Adresszusatz',
        city: 'Stadt',
        postcode: 'Postleitzahl',
        country: 'Land',
        iban: 'IBAN',
        bic: 'BIC'
    };

    /** The one address field on the form, if it has one. */
    const addressField = useMemo(() => mappableFields.find((field) => field.type === 'address'), [mappableFields]);

    // Prefills from the same conventions the import backend uses, then layers
    // any persisted choices over the matching targets.
    const resolveFormMapping = useCallback(() => {
        setValue(namePrefix, null);

        Object.keys(disableGuessing ? {} : mappedFields).forEach((key) => {
            let regex: string | undefined;
            let conventionalName: string | undefined;
            /**
             * Whether every matching field is taken rather than the first one.
             *
             * The exception, not the rule: a target normally has one source, and
             * a second match is a competing candidate the club has to choose
             * between. A form that asks for the group size as one tick box per
             * size — "Einzelstunde ☐ · 2er Gruppe ☑ · 3er Gruppe ☑" — has no
             * competing candidates: all three boxes together are the answer, and
             * prefilling only the first would quietly import a third of it.
             */
            let collectAll = false;

            switch (key) {
                case 'line0':
                    regex = '(name|rechnungsempfänger)';
                    break;
                case 'line1':
                    regex = '(straße|adresse|strasse)';
                    break;
                case 'line2':
                    regex = '(adresszusatz)';
                    break;
                case 'city':
                    regex = '(ort|stadt)';
                    break;
                case 'postcode':
                    regex = '(postleitzahl|plz)';
                    break;
                case 'country':
                    regex = '(land)';
                    break;
                case 'iban':
                    regex = '(iban)';
                    break;
                case 'name':
                    conventionalName = 'trainingplan_name';
                    // Deliberately not matching a bare "vorname"/"nachname": a
                    // form that asks for the two halves separately is mapped by
                    // the two targets below, and a single-field target that ate
                    // the first half would produce a roster of first names.
                    regex =
                        '(teilnehmer|spieler|trainierende).*(^|\\W)(name)|(^|\\W)(name).*(teilnehmer|spieler|trainierende)';
                    break;
                // The pair a registration form actually asks for. Matched on
                // their own words rather than on "name", which both of them
                // contain — "nachname" would otherwise answer to the first-name
                // pattern and every participant would arrive twice-surnamed.
                case 'first_name':
                    conventionalName = 'trainingplan_firstname';
                    regex = '(vorname|first\\s*name|given\\s*name)';
                    break;
                case 'last_name':
                    conventionalName = 'trainingplan_lastname';
                    regex = '(nachname|familienname|last\\s*name|surname|family\\s*name)';
                    break;
                case 'email':
                    conventionalName = 'trainingplan_email';
                    regex = '(e-?mail)';
                    break;
                case 'birth_date':
                    conventionalName = 'trainingplan_birthdate';
                    regex = '(geburtsdatum|geburtstag|birth\\s*date|date\\s*of\\s*birth)';
                    break;
                case 'gender':
                    conventionalName = 'trainingplan_gender';
                    regex = '(geschlecht|gender|anrede|sex)';
                    break;
                case 'sessions':
                    conventionalName = 'trainingplan_trainingscount';
                    regex = '(wie\\s*oft.*woche|anzahl.*training|training.*anzahl)';
                    break;
                // Asked for in two shapes, and both are prefilled from the same
                // pattern: one field holding the answer ("Gruppengröße", a
                // number or a select), or one tick box per size. The pattern
                // deliberately needs a size in the label — a bare "Gruppe" is
                // usually the club's own group names, not a wish.
                case 'group_sizes':
                    collectAll = true;
                    regex =
                        '(gruppengr(ö|oe)(ß|ss)e|\\d\\s*er[\\s-]*(gruppe|training|stunde)' +
                        '|(zweier|dreier|vierer|f(ü|ue)nfer|sechser)[\\s-]*(gruppe|training)' +
                        '|einzel(stunde|training|unterricht)|privatstunde)';
                    break;
                // Every matching table, because forms split the week into two
                // grids when weekends start at other hours, and both carry the
                // same question. The importer merges them into one week.
                case 'timeslots':
                    conventionalName = 'trainingplan_table';
                    regex = '(wann.*train|verf(ü|ue)gbar|availability)';
                    collectAll = !mappableFields.some((candidate) => candidate.name === conventionalName);
                    break;
                case 'remarks':
                    conventionalName = 'trainingplan_remarks';
                    regex = '(bemerkung|feedback|kommentar)';
                    break;
                // Anchored near the start of the label on purpose. "Bemerkungen
                // (z.B. WunschtrainerIn)" is a remarks box naming this as an
                // example of what to write in it — pointing the trainer target
                // at it would read a whole sentence of prose as a list of
                // staff. The head rule the group sizes use is the same idea.
                case 'preferred_trainer':
                    regex = '^[^(]{0,20}(wunsch\\s*-?\\s*trainer|trainerwunsch|preferred\\s*coach)';
                    break;
                case 'wish':
                    conventionalName = 'trainingplan_wish';
                    regex = '(wunschpartner|wunschsch(ü|ue)ler|trainingspartner|gruppenpartner|zusammen\\s*mit)';
                    break;
            }

            // A guess the select would not even offer is no guess at all.
            const matching = (pattern: string, headOnly = false) =>
                mappableFields
                    .filter((candidate) => fieldTypesByKey?.[key]?.includes(candidate.type) ?? true)
                    .filter((candidate) =>
                        `${headOnly ? (candidate.ownLabel ?? '').slice(0, 60) : (candidate.ownLabel ?? '')} ${
                            candidate.name ?? ''
                        }`
                            .toLowerCase()
                            .match(new RegExp(pattern))
                    );

            if (collectAll && regex) {
                /* Matched against the head of the label, not all of it, and
                   only here.

                   For every other target a long label is harmless: the label
                   identifies the field and the answer is read from its value.
                   A target collecting tick boxes reads the label itself, so a
                   consent paragraph that mentions the words halfway through a
                   sentence about something else becomes an answer. Measured
                   over every form in the database, cutting at sixty characters
                   drops 162 of those — "Ich bin damit einverstanden, dass …
                   eine andere Gruppengröße …" on a box meaning "AGB gelesen" —
                   and keeps the long-but-real ones, whose subject is still at
                   the front. The importer reads labels by the same rule. */
                const found = matching(regex, 'group_sizes' === key).map((candidate) => candidate.name);

                // A single match stays a single path: the mapping is stored as
                // it is chosen, and a one-element list would make every reader
                // handle a shape that means nothing different.
                if (found.length) setValue(`${namePrefix}.${key}`, found.length > 1 ? found : found[0]);

                return;
            }

            const matchedField =
                mappableFields.find((candidate) => conventionalName && candidate.name === conventionalName) ??
                (regex ? matching(regex)[0] : undefined);

            if (matchedField) {
                setValue(`${namePrefix}.${key}`, subPath(matchedField, key) ?? matchedField.name);

                return;
            }

            /* One address field answers four targets, and only one of them is
               matched by a label.

               A form asking for the address through the address field has a
               single "Adresse" on it — the regex for `line1` finds it, the ones
               for postcode, city and country find nothing, and the club used to
               end up with invoices carrying a street and no town. So where a
               target found nothing of its own, the address field's part for that
               target stands in. It is the fallback rather than the rule: a form
               that really does ask for street, PLZ and Ort as three text fields
               has matched them above already and keeps them. */
            const fromAddress = subPath(addressField, key);

            if (fromAddress) setValue(`${namePrefix}.${key}`, fromAddress);
        });

        // Persisted choices win per target, but a partial saved mapping must
        // not disable every convention it does not mention. The old training
        // plan importer always fell back one field at a time; doing the same in
        // the form keeps what the selects show aligned with what the backend
        // will actually read.
        Object.entries(defaultMapping ?? {}).forEach(([key, value]) => {
            // An empty list means "not mapped" just like null, so it must not be written either.
            if (toSourceList(value).length) setValue(`${namePrefix}.${key}`, value);
        });
    }, [
        mappableFields,
        addressField,
        defaultMapping,
        namePrefix,
        setValue,
        mappedFields,
        disableGuessing,
        fieldTypesByKey
    ]);

    // Re-runs the prefill whenever the selected form changes, or the backend-provided
    // defaultMapping's content changes (arrives asynchronously after mount for some consumers).
    // An undefined/null defaultMapping never triggers this on its own, so existing consumers that
    // don't pass it keep relying solely on the form-id gate for the invoice heuristic.
    useEffect(() => {
        if (isLoading) return;

        const formChanged = lastFormId.current !== formId;
        const defaultMappingChanged =
            defaultMappingKey !== undefined && defaultMappingKey !== lastDefaultMappingKey.current;
        const hasCurrentMapping =
            currentMapping &&
            'object' === typeof currentMapping &&
            Object.values(currentMapping).some((value) => toSourceList(value as MappingValue).length > 0);

        // Editing an event starts with its persisted invoice mapping already
        // in form state. Do not replace that with guesses merely because this
        // is the component's first render.
        if (formChanged && undefined === lastFormId.current && hasCurrentMapping && !defaultMappingChanged) {
            lastFormId.current = formId;
            lastDefaultMappingKey.current = defaultMappingKey;
            return;
        }

        if (formChanged || defaultMappingChanged) {
            resolveFormMapping();
            lastFormId.current = formId;
            lastDefaultMappingKey.current = defaultMappingKey;
        }
    }, [currentMapping, defaultMappingKey, formId, isLoading, resolveFormMapping]);

    return formId ? (
        <>
            <Spacer y={1} />
            {!hideLabel && (
                <>
                    <label className="tw-m-0 tw-text-[14pd]">Formularzuordnung</label>
                    <br />
                </>
            )}
            <p className="tw-m-0 tw-text-sm tw-text-accents-6">
                {description ??
                    'Hier kannst du zuordnen welche Daten die Felder in deinem Formular wiederspiegeln. Dies' +
                        ' wird später dazu benutzt um automatisiert Rechnungen aus deinen Buchungen zu erstellen.'}
            </p>
            {Object.entries(mappedFields).map(([key, value]) => (
                <Fragment key={key}>
                    {headingsByKey?.[key] && (
                        <p className="tw-mb-1 tw-mt-4 tw-text-sm tw-font-semibold tw-text-accents-8">
                            {headingsByKey[key]}
                        </p>
                    )}
                    <FormMappingRow
                        name={`${namePrefix}.${key}`}
                        label={value}
                        description={descriptionsByKey?.[key]}
                        options={buildFieldOptions(fieldTypesByKey?.[key])}
                        multiple={multiple || multipleKeys?.includes(key)}
                        joinHint={joinHintsByKey?.[key]}
                        slotAnnotations={slotAnnotationsByKey?.[key]}
                        maxSources={maxSources}
                    />
                </Fragment>
            ))}
        </>
    ) : null;
};
